import { useState } from "react";
import { MessageSquareText, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import { SectionTitle } from "./detail-parts";
import { useContractingNotes } from "@/hooks/queries/useContractingNotes";
import {
  useCreateContractingNote,
  useDeleteContractingNote,
  useUpdateContractingNote,
} from "@/hooks/mutations/useContractingNoteMutations";
import type { ContractingNote } from "@/types/domain";

/** Fecha corta con hora, como el resto del historial. */
function formatWhen(iso: string) {
  return new Date(iso).toLocaleString("es-PE", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function NoteRow({
  note,
  contractingId,
  isMine,
}: {
  note: ContractingNote;
  contractingId: number;
  isMine: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const update = useUpdateContractingNote();
  const remove = useDeleteContractingNote();

  function save() {
    const body = draft.trim();
    if (!body || body === note.body) {
      setEditing(false);
      return;
    }
    update.mutate(
      { id: note.id, contracting_id: contractingId, body },
      {
        onSuccess: () => setEditing(false),
        onError: () => toast.error("No se pudo editar el comentario"),
      },
    );
  }

  return (
    <li className="flex gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0 dark:border-slate-800">
      {note.author ? (
        <MemberAvatar member={note.author} className="mt-0.5 size-7 shrink-0" />
      ) : (
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-400 dark:bg-slate-800">
          ?
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {note.author?.name ?? "Nota anterior"}
          </span>
          <span className="text-[11px] text-slate-400">
            {formatWhen(note.createdAt)}
          </span>
          {note.updatedAt !== note.createdAt && (
            <span className="text-[11px] italic text-slate-400">editado</span>
          )}
          {isMine && !editing && (
            <span className="ml-auto flex items-center gap-1">
              <button
                type="button"
                title="Editar"
                onClick={() => {
                  setDraft(note.body);
                  setEditing(true);
                }}
                className="cursor-pointer text-slate-400 transition-colors hover:text-primary"
              >
                <Pencil className="size-3.5" />
              </button>
              <button
                type="button"
                title="Borrar"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(
                    { id: note.id, contracting_id: contractingId },
                    { onError: () => toast.error("No se pudo borrar") },
                  )
                }
                className="cursor-pointer text-slate-400 transition-colors hover:text-red-500"
              >
                <Trash2 className="size-3.5" />
              </button>
            </span>
          )}
        </div>

        {editing ? (
          <div className="mt-1.5 flex flex-col gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="w-full rounded-lg border bg-white p-2 text-sm text-slate-700 focus:border-primary focus:outline-none dark:bg-slate-900/40 dark:text-slate-200"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={save} disabled={update.isPending}>
                Guardar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                <X className="size-3.5" /> Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-600 dark:text-slate-300">
            {note.body}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * Los comentarios de seguimiento de la licitación. Cada uno lleva su autor y su
 * fecha, y solo su autor lo edita: antes era un campo único que dos personas se
 * pisaban sin enterarse.
 */
export function NotesCard({ contractingId }: { contractingId: number }) {
  const [draft, setDraft] = useState("");
  const { data: session } = authClient.useSession();
  const { data: notes, isLoading } = useContractingNotes(contractingId);
  const create = useCreateContractingNote();

  function add() {
    const body = draft.trim();
    if (!body) return;
    create.mutate(
      { contracting_id: contractingId, body },
      {
        onSuccess: () => setDraft(""),
        onError: () => toast.error("No se pudo guardar el comentario"),
      },
    );
  }

  return (
    <div className="flex flex-col">
      <SectionTitle
        icon={<MessageSquareText className="size-4 text-blue-500" />}
      >
        Comentarios del equipo
      </SectionTitle>
      <div className="mt-3 flex flex-col rounded-2xl border bg-card p-5">
        {/* Una línea mientras está vacío y crece al escribir: vacío no justifica tres.
            En una línea el botón va centrado; al crecer, abajo junto al texto. */}
        <div className={cn("flex gap-2", draft ? "items-end" : "items-center")}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={draft ? 3 : 1}
            placeholder="Escribe qué pasó. Lo ve todo el equipo."
            title="Queda firmado con tu nombre y la fecha."
            className="min-w-0 flex-1 resize-none rounded-xl border bg-slate-50/50 px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none dark:bg-slate-900/10 dark:text-slate-200"
          />
          <Button
            size="sm"
            onClick={add}
            disabled={create.isPending || !draft.trim()}
            className="shrink-0 cursor-pointer"
          >
            {create.isPending ? "Guardando..." : "Comentar"}
          </Button>
        </div>

        <div className="mt-2">
          {isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !notes?.length ? (
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Todavía no hay comentarios en esta oportunidad.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {notes.map((note) => (
                <NoteRow
                  key={note.isLegacy ? "legacy" : note.id}
                  note={note}
                  contractingId={contractingId}
                  isMine={
                    !note.isLegacy && note.author?.id === session?.user.id
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
